/**
 * Generic modal dialog with drag-to-reposition.
 *
 * Usage:
 *   const modal = new Modal('my-modal', { title: 'Hello', onClose });
 *   modal.setBody('<p>Content here</p>');
 *   modal.open();
 *   modal.close();
 */
export class Modal {
  constructor(id, { title = '', onClose } = {}) {
    this.id      = id;
    this.title   = title;
    this.onClose = onClose;
    this.el      = null;
    this._dragging = false;
    this._dragOffset = { x: 0, y: 0 };
    this._create();
  }

  _create() {
    this.el = document.createElement('div');
    this.el.id        = this.id;
    this.el.className = 'modal-overlay';
    this.el.innerHTML = `
      <div class="modal-box">
        <div class="modal-header">
          <h3 class="modal-title">${this.title}</h3>
          <button class="modal-close-btn">&times;</button>
        </div>
        <div class="modal-body"></div>
      </div>`;

    this.el.querySelector('.modal-close-btn').addEventListener('click', () => this.close());
    this.el.addEventListener('click', (e) => {
      if (e.target === this.el) this.close(); // click backdrop
    });

    this._initDrag();

    document.getElementById('modal-container').appendChild(this.el);
  }

  _initDrag() {
    const header = this.el.querySelector('.modal-header');
    const box = this.el.querySelector('.modal-box');

    const onStart = (clientX, clientY) => {
      const rect = box.getBoundingClientRect();
      this._dragging = true;
      this._dragOffset.x = clientX - rect.left;
      this._dragOffset.y = clientY - rect.top;
      box.style.transition = 'none';
      header.style.cursor = 'grabbing';
    };

    const onMove = (clientX, clientY) => {
      if (!this._dragging) return;
      const x = clientX - this._dragOffset.x;
      const y = clientY - this._dragOffset.y;
      // Switch from flex-centered to absolute positioning on first drag
      if (!box.classList.contains('modal-dragged')) {
        box.classList.add('modal-dragged');
      }
      box.style.left = x + 'px';
      box.style.top  = y + 'px';
    };

    const onEnd = () => {
      if (!this._dragging) return;
      this._dragging = false;
      box.style.transition = '';
      header.style.cursor = '';
    };

    // Mouse events
    header.addEventListener('mousedown', (e) => {
      if (e.target.closest('.modal-close-btn')) return;
      e.preventDefault();
      onStart(e.clientX, e.clientY);
    });
    window.addEventListener('mousemove', (e) => onMove(e.clientX, e.clientY));
    window.addEventListener('mouseup', onEnd);

    // Touch events
    header.addEventListener('touchstart', (e) => {
      if (e.target.closest('.modal-close-btn')) return;
      const t = e.touches[0];
      onStart(t.clientX, t.clientY);
    }, { passive: true });
    window.addEventListener('touchmove', (e) => {
      if (!this._dragging) return;
      const t = e.touches[0];
      onMove(t.clientX, t.clientY);
    }, { passive: true });
    window.addEventListener('touchend', onEnd);
  }

  _resetPosition() {
    const box = this.el.querySelector('.modal-box');
    box.classList.remove('modal-dragged');
    box.style.left = '';
    box.style.top  = '';
  }

  setTitle(t) {
    this.el.querySelector('.modal-title').textContent = t;
  }

  setBody(html) {
    this.el.querySelector('.modal-body').innerHTML = html;
  }

  open() {
    this._resetPosition();
    this.el.classList.add('active');
  }

  close() {
    this.el.classList.remove('active');
    if (this.onClose) this.onClose();
  }

  isOpen() {
    return this.el.classList.contains('active');
  }
}
